// AnsageStationPage.jsx — der Rechner an der Lautsprecheranlage.
//
// Diese Seite läuft am Turniertag auf einem Laptop, der am Mischpult hängt.
// Sie holt Ansagen, spricht sie und meldet sie zurück.
//
// ⚠️ EINE Durchsage blockiert die nächste. Die Station holt genau eine Ansage,
// spielt Gong → Deutsch → Englisch, meldet sie und holt erst dann die nächste.
// Zwei Stimmen übereinander versteht niemand.
//
// ⚠️ Der Browser spielt keinen Ton, bevor jemand geklickt hat — Autoplay-Sperre.
// Deshalb steht am Anfang ein grosser Knopf „Anlage starten". Ohne ihn bliebe
// die Halle still, und im Log stünde nichts: der schlimmste Fehler von allen,
// weil man ihn erst merkt, wenn jemand nicht aufgerufen wurde.
//
// ⚠️ Bedienbar ohne Erklärung. Am Testturnier sitzt dort jemand, der die Seite
// zum ersten Mal sieht: grosse Knöpfe, Klartext statt Symbolen, und jederzeit
// sichtbar, was gerade passiert.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './AnsageStationPage.css';

const PAUSE_ZWISCHEN_SPRACHEN_MS = 400;
const POLL_MS = 2000;
const WARTE_WARNUNG_S = 30;

const kopf = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/** Kurzer Gong über die Web-Audio-Schnittstelle — keine Datei nötig. */
function gongSpielen(ctx, lautstaerke) {
  return new Promise((fertig) => {
    if (!ctx) return fertig();
    const jetzt = ctx.currentTime;
    const huelle = ctx.createGain();
    huelle.connect(ctx.destination);
    huelle.gain.setValueAtTime(0.0001, jetzt);
    huelle.gain.exponentialRampToValueAtTime(Math.max(0.0001, lautstaerke * 0.5), jetzt + 0.02);
    huelle.gain.exponentialRampToValueAtTime(0.0001, jetzt + 1.1);
    // Zwei Töne im Quintabstand: trägt durch eine laute Halle besser als einer.
    for (const hz of [784, 523.25]) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = hz;
      o.connect(huelle);
      o.start(jetzt);
      o.stop(jetzt + 1.2);
    }
    setTimeout(fertig, 900);
  });
}

export default function AnsageStationPage() {
  const { turnierId } = useParams();

  const [gestartet, setGestartet]   = useState(false);
  const [pausiert, setPausiert]     = useState(false);
  const [aktuell, setAktuell]       = useState(null);
  const [phase, setPhase]           = useState('');      // was gerade läuft
  const [schlange, setSchlange]     = useState([]);
  const [wartet, setWartet]         = useState(0);
  const [lautstaerke, setLautstaerke] = useState(1);
  // ⚠️ MEHRERE Matten, nicht eine. Am Turniertag bedient eine Station oft
  // einen Block („1 bis 3 hier, 4 bis 6 drüben"). Mit einem Auswahlfeld gäbe
  // es nur „genau eine" oder „alle" — dazwischen liegt der Normalfall.
  const [matten, setMatten]         = useState([]);   // leer = alle
  const [alleMatten, setAlleMatten] = useState([]);   // was das Turnier hat
  const [bereit, setBereit]         = useState(null);
  const [fehler, setFehler]         = useState('');
  const [letzte, setLetzte]         = useState(null);
  const [verlauf, setVerlauf]       = useState([]);

  // Refs, weil die Abspielschleife ausserhalb des Renderns läuft und sonst
  // veraltete Werte sähe.
  const audioCtx   = useRef(null);
  const audioEl    = useRef(null);
  const beschaeftigt = useRef(false);
  const pausiertRef  = useRef(false);
  const lautRef      = useRef(1);
  const abbruch      = useRef(false);
  const laufendRef   = useRef(null);

  useEffect(() => { pausiertRef.current = pausiert; }, [pausiert]);
  useEffect(() => {
    lautRef.current = lautstaerke;
    if (audioEl.current) audioEl.current.volume = lautstaerke;
  }, [lautstaerke]);

  // ── Bereitschaft und Warteschlange ──────────────────────────────────────
  const ladeBereit = useCallback(async () => {
    try {
      const r = await fetch(`/api/ansagen/turnier/${turnierId}/bereit`, { headers: kopf() });
      const d = await r.json();
      if (d.success) setBereit(d);
      else setFehler(d.error || 'Bereitschaft nicht abrufbar');
    } catch { setFehler('Server nicht erreichbar'); }
  }, [turnierId]);

  // Die vorhandenen Matten laden — nicht 1 bis 10 raten. Ein Turnier mit drei
  // Tatamis soll keine sieben Haken anbieten, die nie etwas liefern.
  useEffect(() => {
    let laeuft = true;
    fetch(`/api/kampfflaechen/${turnierId}`, { headers: kopf() })
      .then((r) => r.json())
      .then((d) => {
        if (!laeuft) return;
        const liste = (d.data || d.kampfflaechen || d || [])
          .map((k) => k.nummer).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
        setAlleMatten(liste);
      })
      .catch(() => {});
    return () => { laeuft = false; };
  }, [turnierId]);

  const ladeSchlange = useCallback(async () => {
    try {
      const q = matten.length ? `?matten=${matten.join(',')}` : '';
      const r = await fetch(`/api/ansagen/turnier/${turnierId}/warteschlange${q}`, { headers: kopf() });
      const d = await r.json();
      if (d.success) { setSchlange(d.ansagen || []); setWartet(d.wartet_sekunden || 0); }
    } catch { /* der nächste Durchlauf versucht es wieder */ }
  }, [turnierId, matten]);

  useEffect(() => { ladeBereit(); }, [ladeBereit]);
  useEffect(() => {
    ladeSchlange();
    const i = setInterval(ladeSchlange, 3000);
    return () => clearInterval(i);
  }, [ladeSchlange]);

  // ── Abspielen ───────────────────────────────────────────────────────────

  /** Spielt eine Tondatei. Löst auch bei Fehler auf — die Anlage darf nie hängen. */
  const dateiSpielen = useCallback((datei) => new Promise((fertig) => {
    if (!datei) return fertig(false);
    const el = audioEl.current;
    if (!el) return fertig(false);
    const aufraeumen = () => {
      el.onended = null; el.onerror = null;
      fertig(true);
    };
    el.onended = aufraeumen;
    el.onerror = () => { el.onended = null; el.onerror = null; fertig(false); };
    el.src = `/api/ansagen/ton/${datei}`;
    el.volume = lautRef.current;
    el.play().catch(() => { el.onended = null; el.onerror = null; fertig(false); });
  }), []);

  /**
   * Rückfall auf die Browserstimme.
   *
   * ⚠️ Klingt deutlich schlechter als Piper — aber eine schlechte Ansage ist
   * besser als eine ausgefallene Anlage. Passiert nur, wenn die Tondatei fehlt.
   */
  const browserSprechen = useCallback((text, sprache) => new Promise((fertig) => {
    if (!text || !window.speechSynthesis) return fertig(false);
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = sprache === 'en' ? 'en-US' : 'de-DE';
      u.volume = lautRef.current;
      u.onend = () => fertig(true);
      u.onerror = () => fertig(false);
      window.speechSynthesis.speak(u);
    } catch { fertig(false); }
  }), []);

  const warte = (ms) => new Promise((r) => setTimeout(r, ms));

  const ansageSprechen = useCallback(async (a) => {
    setPhase('Gong');
    await gongSpielen(audioCtx.current, lautRef.current);
    if (abbruch.current) return false;

    setPhase('Deutsch');
    const deOk = await dateiSpielen(a.audio_de);
    if (!deOk && !abbruch.current) await browserSprechen(a.text_de, 'de');
    if (abbruch.current) return false;

    if (a.text_en) {
      await warte(PAUSE_ZWISCHEN_SPRACHEN_MS);
      if (abbruch.current) return false;
      setPhase('Englisch');
      const enOk = await dateiSpielen(a.audio_en);
      if (!enOk && !abbruch.current) await browserSprechen(a.text_en, 'en');
    }
    return !abbruch.current;
  }, [dateiSpielen, browserSprechen]);

  const melden = useCallback(async (id, gesprochen) => {
    try {
      await fetch(`/api/ansagen/${id}/${gesprochen ? 'gesprochen' : 'verworfen'}`,
        { method: 'POST', headers: kopf() });
    } catch { /* das Aufräumen im Server gibt sie nach 60 s selbst frei */ }
  }, []);

  // ⚠️ Beim Schliessen oder Neuladen die laufende Ansage freigeben.
  //
  // Ohne das bliebe sie 60 Sekunden auf 'laeuft' stehen (bis das Aufräumen im
  // Server greift) — und genau so lange schweigt die Anlage. Am Testturnier
  // wird jemand die Seite neu laden; das darf keine Minute Stille kosten.
  //
  // `sendBeacon`, weil ein normales fetch beim Entladen der Seite abgebrochen
  // wird. Es schickt keinen Authorization-Kopf mit, deshalb hängt der Token an
  // der Adresse — die Route erlaubt das eigens für diesen Fall.
  useEffect(() => {
    const beimVerlassen = () => {
      const a = laufendRef.current;
      if (!a) return;
      try {
        const token = localStorage.getItem('token');
        navigator.sendBeacon(
          `/api/ansagen/${a.ansage_id}/verworfen?beacon=${encodeURIComponent(token || '')}`
        );
      } catch { /* dann greift nach 60 s das Aufräumen im Server */ }
    };
    window.addEventListener('pagehide', beimVerlassen);
    return () => {
      window.removeEventListener('pagehide', beimVerlassen);
      beimVerlassen();
    };
  }, []);

  // Die Schleife: holen → sprechen → melden → von vorn.
  useEffect(() => {
    if (!gestartet) return undefined;
    let laeuft = true;

    const runde = async () => {
      if (!laeuft || beschaeftigt.current || pausiertRef.current) return;
      beschaeftigt.current = true;
      abbruch.current = false;
      try {
        const q = matten.length ? `?matten=${matten.join(',')}` : '';
        const r = await fetch(`/api/ansagen/turnier/${turnierId}/naechste${q}`, { headers: kopf() });
        const d = await r.json();
        if (!d.success || !d.ansage) return;

        const a = d.ansage;
        laufendRef.current = a;
        setAktuell(a);
        setFehler('');
        const fertig = await ansageSprechen(a);
        await melden(a.ansage_id, fertig);
        laufendRef.current = null;
        setLetzte(a);
        setVerlauf((v) => [{ ...a, wann: new Date(), uebersprungen: !fertig }, ...v].slice(0, 12));
      } catch {
        setFehler('Server nicht erreichbar — es wird weiter versucht');
      } finally {
        setAktuell(null);
        setPhase('');
        beschaeftigt.current = false;
      }
    };

    runde();
    const i = setInterval(runde, POLL_MS);
    return () => { laeuft = false; clearInterval(i); };
  }, [gestartet, matten, turnierId, ansageSprechen, melden]);

  // ── Bedienung ───────────────────────────────────────────────────────────

  const anlageStarten = async () => {
    // ⚠️ Der Ton-Zusammenhang MUSS in einem Klick entstehen, sonst bleibt er
    // gesperrt. Das ist der ganze Zweck dieses Knopfes.
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      audioCtx.current = new Ctx();
      if (audioCtx.current.state === 'suspended') await audioCtx.current.resume();
    } catch { /* ohne Gong geht es auch */ }
    setGestartet(true);
  };

  const ueberspringen = () => {
    abbruch.current = true;
    if (audioEl.current) { audioEl.current.pause(); audioEl.current.currentTime = 0; }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  };

  const wiederholen = async () => {
    if (!letzte || beschaeftigt.current) return;
    beschaeftigt.current = true;
    abbruch.current = false;
    setAktuell(letzte);
    await ansageSprechen(letzte);
    setAktuell(null);
    setPhase('');
    beschaeftigt.current = false;
  };

  /**
   * Probeansage — prüft die GANZE Kette, nicht nur die Lautsprecher.
   *
   * ⚠️ Sie sprach zuerst mit der Browserstimme. Das war ein Denkfehler: Damit
   * beweist die Probe nur, dass Ton aus den Boxen kommt. Wäre Piper auf dem
   * Server kaputt, klänge sie genauso — und es fiele erst beim ersten echten
   * Aufruf auf, vor voller Halle.
   *
   * Jetzt geht sie den normalen Weg: als Ansage in die Warteschlange, vom
   * Server mit Piper gesprochen, von der Station abgeholt und abgespielt. Wenn
   * das klingt, klingt am Turniertag auch alles andere.
   */
  const probeansage = async () => {
    if (beschaeftigt.current) return;
    // ⚠️ Pausiert holt die Schleife nichts ab — die Probe laege stumm in der
    // Warteschlange, und man suchte den Fehler bei Piper.
    if (pausiert) {
      setFehler('Die Station ist angehalten — erst „Weiter", dann die Probeansage.');
      return;
    }
    setPhase('Probe wird erzeugt …');
    setFehler('');
    try {
      const r = await fetch(`/api/ansagen/turnier/${turnierId}/frei`, {
        method: 'POST',
        headers: { ...kopf(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: 'Probeansage. Die Anlage ist bereit und spricht über den Server.',
        }),
      });
      const d = await r.json();
      if (!d.success) {
        setFehler(d.error || 'Probeansage konnte nicht eingereiht werden');
        setPhase('');
        return;
      }
      // Die Abspielschleife holt sie beim nächsten Durchlauf von selbst ab.
      // Auf dem Server dauert das Erzeugen drei bis acht Sekunden.
      setPhase('Probe wird erzeugt … (bis zu 10 Sekunden)');
      setTimeout(() => setPhase((p) => (p.startsWith('Probe') ? '' : p)), 12000);
    } catch {
      setFehler('Server nicht erreichbar — Probeansage nicht möglich');
      setPhase('');
    }
  };

  // ── Anzeige ─────────────────────────────────────────────────────────────

  if (!gestartet) {
    return (
      <div className="ast ast--start">
        <div className="ast-startkarte">
          <h1>Ansage-Station</h1>
          <p className="ast-start-text">
            Dieser Rechner spricht die Aufrufe über die Lautsprecheranlage.
            Zuerst Lautstärke am Mischpult einstellen, dann starten.
          </p>
          <button className="ast-btn ast-btn--gross" onClick={anlageStarten}>
            Anlage starten
          </button>
          <p className="ast-start-hinweis">
            Der Browser lässt keinen Ton zu, bevor hier geklickt wurde.
            Ohne diesen Schritt bleibt die Halle still.
          </p>
          {bereit && !bereit.ton_bereit && (
            <div className="ast-warnung">
              ⚠️ Die Sprachausgabe ist auf dem Server nicht eingerichtet.
              Es wird mit der Browserstimme gesprochen — das klingt deutlich schlechter.
              <ul>{bereit.fehlend.map((f) => <li key={f}>{f}</li>)}</ul>
            </div>
          )}
          {bereit && !bereit.eingeschaltet && (
            <div className="ast-warnung">
              ⚠️ Für dieses Turnier ist die automatische Ansage ausgeschaltet.
              Es entstehen keine Aufrufe.
            </div>
          )}
        </div>
        <audio ref={audioEl} preload="auto" />
      </div>
    );
  }

  return (
    <div className="ast">
      <header className="ast-kopf">
        <div className="ast-kopf-links">
          <span className={`ast-lampe ${aktuell ? 'ast-lampe--an' : ''}`} />
          <strong>Ansage-Station</strong>
          <span className="ast-turnier">Turnier {turnierId}</span>
          {matten.length > 0 && (
            <span className="ast-mattenmarke">
              nur Matte {matten.join(', ')}
            </span>
          )}
        </div>
        <div className="ast-kopf-rechts">
          {pausiert
            ? <span className="ast-zustand ast-zustand--pause">Pausiert</span>
            : <span className="ast-zustand">Bereit</span>}
          {/* ⚠️ Ein Weg zurueck muss da sein. Die Station wird meist in einem
              eigenen Fenster geoeffnet — wer sie aber im selben Tab aufruft,
              sass sonst in einer Sackgasse. */}
          <a className="ast-zurueck" href="/dashboard">← Dashboard</a>
        </div>
      </header>

      {fehler && <div className="ast-fehler" role="alert">{fehler}</div>}

      {wartet > WARTE_WARNUNG_S && (
        <div className="ast-warnung">
          ⚠️ Die älteste Ansage wartet seit {wartet} Sekunden. Die Ansagen kommen
          dem Turnier hinterher — entweder auf „nur Deutsch" umstellen oder eine
          zweite Station für eine andere Matte aufmachen.
        </div>
      )}

      <section className="ast-buehne">
        {aktuell ? (
          <>
            <div className="ast-phase">{phase}</div>
            <div className="ast-text-de">{aktuell.text_de}</div>
            {aktuell.text_en && <div className="ast-text-en">{aktuell.text_en}</div>}
            {!aktuell.audio_de && (
              <div className="ast-notstimme">
                Tondatei fehlt — es spricht die Browserstimme
              </div>
            )}
          </>
        ) : (
          <div className="ast-leer">
            {pausiert
              ? 'Pausiert — es wird nichts gesprochen.'
              : schlange.length > 0
                ? 'Nächste Ansage wird geholt …'
                : 'Keine Ansage offen. Die Station wartet.'}
          </div>
        )}
      </section>

      <section className="ast-bedienung">
        <button
          className={`ast-btn ${pausiert ? 'ast-btn--gruen' : 'ast-btn--rot'}`}
          onClick={() => setPausiert((p) => !p)}
        >
          {pausiert ? 'Weiter' : 'Alles anhalten'}
        </button>
        <button className="ast-btn" onClick={ueberspringen} disabled={!aktuell}>
          Überspringen
        </button>
        <button className="ast-btn" onClick={wiederholen} disabled={!letzte || !!aktuell}>
          Wiederholen
        </button>
        <button className="ast-btn" onClick={probeansage} disabled={!!aktuell}>
          Probeansage
        </button>

        <label className="ast-regler">
          Lautstärke
          <input
            type="range" min="0" max="1" step="0.05" value={lautstaerke}
            onChange={(e) => setLautstaerke(parseFloat(e.target.value))}
          />
          <span className="ast-regler-wert">{Math.round(lautstaerke * 100)} %</span>
        </label>

        <div className="ast-matten">
          <span className="ast-matten-titel">Matten</span>
          <label className="ast-matte-haken">
            <input
              type="checkbox"
              checked={matten.length === 0}
              onChange={() => setMatten([])}
            />
            alle
          </label>
          {alleMatten.map((n) => (
            <label key={n} className="ast-matte-haken">
              <input
                type="checkbox"
                checked={matten.includes(n)}
                onChange={(e) => setMatten((alt) => (e.target.checked
                  ? [...alt, n].sort((a, b) => a - b)
                  : alt.filter((m) => m !== n)))}
              />
              {n}
            </label>
          ))}
        </div>
      </section>

      <section className="ast-spalten">
        <div className="ast-spalte">
          <h2>Wartet <span className="ast-zahl">{schlange.length}</span></h2>
          {schlange.length === 0 && <p className="ast-leise">Nichts offen.</p>}
          <ul className="ast-liste">
            {schlange.map((a) => (
              <li key={a.ansage_id} className={`ast-rang-${a.prioritaet}`}>
                <span className="ast-marke">{a.matte_nummer ? `M${a.matte_nummer}` : '–'}</span>
                <span className="ast-zeile-text">{a.text_de}</span>
                {a.status === 'laeuft' && <span className="ast-laeuft">läuft</span>}
              </li>
            ))}
          </ul>
        </div>

        <div className="ast-spalte">
          <h2>Gesprochen</h2>
          {verlauf.length === 0 && <p className="ast-leise">Noch nichts.</p>}
          <ul className="ast-liste ast-liste--verlauf">
            {verlauf.map((a, i) => (
              <li key={`${a.ansage_id}-${i}`}>
                <span className="ast-uhr">
                  {a.wann.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
                <span className="ast-zeile-text">{a.text_de}</span>
                {a.uebersprungen && <span className="ast-uebersprungen">übersprungen</span>}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <audio ref={audioEl} preload="auto" />
    </div>
  );
}
